import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { BrandMark, Fhi360Badge } from "@/components/Logo";
import { ESPACES, ACCENT, visibleEspaces } from "@/lib/nav";
import { Home, LogOut } from "lucide-react";

const PROFIL_STYLE = {
  admin_si: "bg-blue-50 text-blue-700 border-blue-200", direction: "bg-indigo-50 text-indigo-700 border-indigo-200",
  coord_projet: "bg-amber-50 text-amber-700 border-amber-200", data_manager: "bg-sky-50 text-sky-700 border-sky-200",
  superviseur_zone: "bg-teal-50 text-teal-700 border-teal-200", agent_terrain: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partenaire_lecture: "bg-slate-100 text-slate-600 border-slate-200",
};

function Item({ to, icon: Icon, label, testid, end, accent }) {
  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) => `flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] transition-colors ${
        isActive ? "bg-[#0B2A5B] text-white font-semibold" : "text-slate-600 font-medium hover:bg-slate-100 hover:text-slate-900"}`}>
      {({ isActive }) => (<><Icon style={{ width: 16, height: 16 }} strokeWidth={2} className={!isActive && accent ? accent : ""} />{label}</>)}
    </NavLink>
  );
}

function Section({ esp, t }) {
  const a = ACCENT[esp.accent];
  return (
    <div className="pt-4" data-testid={`nav-section-${esp.key}`}>
      <div className="flex items-center gap-2 px-3 pb-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} />
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">{t(esp.label)}</span>
      </div>
      <div className="space-y-0.5">
        {esp.pages.map((p) => <Item key={p.slug} to={`/app/${esp.key}/${p.slug}`} icon={p.icon} label={t(p.label)} testid={`nav-${esp.key}-${p.slug}`} />)}
      </div>
    </div>
  );
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const espaces = visibleEspaces(user);

  return (
    <aside className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col fixed h-screen z-20" data-testid="sidebar">
      <div className="px-5 h-14 flex items-center border-b border-slate-200"><BrandMark /></div>
      <nav className="flex-1 px-3 py-3 overflow-y-auto">
        <Item to="/app" end icon={Home} label={t("nav_home")} testid="nav-home" />
        {espaces.map((e) => <Section key={e.key} esp={e} t={t} />)}
      </nav>
      <div className="px-4 py-4 border-t border-slate-200 space-y-3">
        <Fhi360Badge />
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#0B2A5B] grid place-items-center text-xs font-semibold text-white shrink-0">{user.name?.slice(0, 2).toUpperCase()}</div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-slate-900 truncate">{user.name}</div>
            <span data-testid="role-badge" className={`inline-block mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border ${PROFIL_STYLE[user.profil] || PROFIL_STYLE.partenaire_lecture}`}>
              {t(`profil_${user.profil}`)}
            </span>
          </div>
        </div>
        <button data-testid="logout-button" onClick={async () => { if ((await logout()) !== false) nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900 transition-colors">
          <LogOut className="w-4 h-4" /> {t("logout")}
        </button>
      </div>
    </aside>
  );
}

export { ESPACES };
